import { useEffect, useState } from 'react'
import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { VectorField } from './WayfindingNodes'
import { cameraController } from '../../lib/cameraController'
import { CAMERA_ACCENT as CAM_ACCENT } from '../../lib/cameraTheme'
import { headTrackedCamera, HEAD_TRACK_DEFAULTS } from '../../lib/headTrackedCamera'

/**
 * Camera nodes (camera-in-blueprint). Exec actions that drive the
 * cameraController singleton (src/lib/cameraController.js), which applies to
 * drei's OrbitControls + the camera each frame. One getter emits the live
 * camera transform. Runtime lives in EventRuntime.js (setters in the exec
 * handler, getter in _resolveDataPin).
 *
 * Blueprint values override the LiveTunePanel unless the dev has touched a
 * control this session — precedence handled in liveTuneStore.setCameraFromBlueprint.
 */

const vecPin = { background: '#60a0d0', width: 10, height: 10, borderRadius: '50%', border: '2px solid #60a0d044' }
const selectStyle = {
  width: '100%', boxSizing: 'border-box', background: '#04121a', border: '1px solid #14384f',
  color: '#8ad0e0', padding: '3px 6px', borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)', outline: 'none',
}
const stop = e => e.stopPropagation()
const execStyle = { background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }

function Shell({ selected, fired, icon, label, tip, exec, minWidth = 210, suppressed, headerExtra, children }) {
  return (
    <div title={tip} style={{
      background: selected ? '#08181f' : '#050f14',
      border: `2px solid ${fired ? '#ffcc44' : selected ? CAM_ACCENT : '#123240'}`,
      borderRadius: 6, minWidth, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: selected ? '#0e2a35' : '#0a1f28', padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        {exec && <Handle type="target" position={Position.Left} id="execIn" style={execStyle} />}
        <span style={{ fontSize: 13 }}>{icon}</span>
        <span style={{ color: CAM_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>{label}</span>
        {/* headerExtra (live-state readout + actions, e.g. Head-Tracked Camera's
            state text + Recenter button) and the rig-motion suppressed badge
            share the same right-aligned slot — no node uses both at once. */}
        {(headerExtra || suppressed) && (
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {headerExtra}
            {suppressed && (
              <span
                title={suppressed === 'follow'
                  ? 'Follow owns the camera — this node is skipped.'
                  : 'Camera control is stood down — this node is skipped.'}
                style={{ fontSize: 11, color: '#f0a860', cursor: 'help' }}
              >⏸</span>
            )}
          </span>
        )}
        {exec && <Handle type="source" position={Position.Right} id="execOut" style={execStyle} />}
      </div>
      {children}
    </div>
  )
}

// Live suppression readout for the rig-motion nodes (Add Yaw/Pitch/Dolly, Pan Target).
// These nodes keep executing (and flashing "fired") while follow or a bake stand-down
// owns the camera — deliberately, see cameraController.motionSuppressed() — so the badge
// is the only thing telling the author why their drag does nothing. Polled like every
// other live-state read in the editor (the render loop never waits on React); setState
// bails to the same value between transitions, so idle polls don't re-render.
function useMotionSuppressed() {
  const [why, setWhy] = useState(() => cameraController.motionSuppressed())
  useEffect(() => {
    const id = setInterval(() => setWhy(cameraController.motionSuppressed()), 250)
    return () => clearInterval(id)
  }, [])
  return why
}

// Live state readout for the Head-Tracked Camera node's header (idle / armed /
// starting / tracking / blocked / error — see headTrackedCamera.js's state
// machine doc comment). Same poll-not-subscribe pattern as useMotionSuppressed:
// the runtime is a module singleton read on the render hot path, so the editor
// polls rather than forcing a re-render pathway into it.
function useHeadTrackState() {
  const [state, setState] = useState(() => headTrackedCamera.state)
  useEffect(() => {
    const id = setInterval(() => setState(headTrackedCamera.state), 250)
    return () => clearInterval(id)
  }, [])
  return state
}

const numStyle = {
  width: '100%', boxSizing: 'border-box', background: '#04121a', border: '1px solid #14384f',
  color: '#8ad0e0', padding: '3px 6px', borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)',
  outline: 'none', textAlign: 'right',
}

// Local number field with an optional data pin (WayfindingNodes' NumField isn't
// exported). Mirrors its behaviour: pin on the left, label, numeric input.
function NumField({ label, pinId, value, onChange }) {
  return (
    <div className="nodrag nowheel"
      style={{ padding: '4px 12px', position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}
      onPointerDown={stop} onMouseDown={stop} onClick={stop}>
      {pinId && (
        <Handle type="target" position={Position.Left} id={pinId}
          style={{ background: '#60a0d0', width: 10, height: 10, borderRadius: '50%', border: '2px solid #60a0d044' }} />
      )}
      <span style={{ color: '#3a7080', fontSize: 10, minWidth: 56 }}>{label}</span>
      <input type="number" step={0.1} value={value}
        onChange={e => { const v = parseFloat(e.target.value); onChange(Number.isFinite(v) ? v : 0) }}
        onPointerDown={stop} onMouseDown={stop} onClick={stop} onKeyDown={stop} onWheel={stop}
        style={numStyle} />
    </div>
  )
}

function Row({ label, children }) {
  return (
    <div className="nodrag nowheel"
      style={{ padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 8 }}
      onPointerDown={stop} onMouseDown={stop} onClick={stop}>
      <span style={{ color: '#3a7080', fontSize: 10, minWidth: 56 }}>{label}</span>
      {children}
    </div>
  )
}

function EnumField({ label, value, options, onChange }) {
  return (
    <Row label={label}>
      <select className="nodrag" value={value} onMouseDown={stop} onChange={e => onChange(e.target.value)} style={selectStyle}>
        {options.map(o => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </Row>
  )
}

function CheckField({ label, checked, onChange }) {
  return (
    <Row label={label}>
      <input type="checkbox" className="nodrag" checked={!!checked}
        onMouseDown={stop} onChange={e => onChange(e.target.checked)}
        style={{ accentColor: CAM_ACCENT }} />
    </Row>
  )
}

// ── Rig motion (camera-in-blueprint) ─────────────────────────────────────────
// These drive the camera rig directly — the state that replaces OrbitControls' internal
// spherical. Wire an input node's delta into in-delta; `sensitivity` scales raw canvas
// pixels into radians (or into the dolly exponent). Together with the input nodes these
// ARE the camera: there is no hidden preset underneath.

export function CameraAddYawNode({ data, selected }) {
  return (
    <Shell exec selected={selected} fired={isFired(data)} suppressed={useMotionSuppressed()} icon="↻" label="Camera Add Yaw" minWidth={228}
      tip="Orbit the camera horizontally around its target by in-delta × sensitivity radians. Wire On Pointer Drag's out-dx here. Yaw is unbounded — you can orbit forever. Damp is the glide left after the drag ends: 0 lands the whole move on the frame it arrives, 0.95 (the shipped feel) eases it in over ~1s. It changes WHEN the motion arrives, never how much.">
      <NumField label="Delta" pinId="in-delta" value={data.delta ?? 0} onChange={v => data.onChange?.('delta', v)} />
      <NumField label="Sens" value={data.sensitivity ?? 0.005} onChange={v => data.onChange?.('sensitivity', v)} />
      <NumField label="Damp" pinId="in-damping" value={data.damping ?? 0.95} onChange={v => data.onChange?.('damping', v)} />
      <CheckField label="Invert" checked={data.invert} onChange={v => data.onChange?.('invert', v)} />
    </Shell>
  )
}

export function CameraAddPitchNode({ data, selected }) {
  return (
    <Shell exec selected={selected} fired={isFired(data)} suppressed={useMotionSuppressed()} icon="↕" label="Camera Add Pitch" minWidth={228}
      tip="Orbit the camera vertically by in-delta × sensitivity radians. Wire On Pointer Drag's out-dy here. Clamped by the rig's pitch limits (Camera Set Rig Limits), and never quite reaches the poles. Damp is the glide left after the drag ends (0 = instant, 0.95 = the shipped feel); a glide into a pitch limit decays against it rather than bouncing.">
      <NumField label="Delta" pinId="in-delta" value={data.delta ?? 0} onChange={v => data.onChange?.('delta', v)} />
      <NumField label="Sens" value={data.sensitivity ?? 0.005} onChange={v => data.onChange?.('sensitivity', v)} />
      <NumField label="Damp" pinId="in-damping" value={data.damping ?? 0.95} onChange={v => data.onChange?.('damping', v)} />
      <CheckField label="Invert" checked={data.invert} onChange={v => data.onChange?.('invert', v)} />
    </Shell>
  )
}

export function CameraAddDollyNode({ data, selected }) {
  return (
    <Shell exec selected={selected} fired={isFired(data)} suppressed={useMotionSuppressed()} icon="🔍" label="Camera Add Dolly" minWidth={228}
      tip="Move the camera toward/away from its target. Multiplicative (dist ×= e^(delta×sens)), so one wheel notch covers the same visual fraction whether you're 0.5m or 50m out. Wire On Wheel's out-delta here. Bounded by the rig's distance limits. Damp defaults to 0 (instant) because OrbitControls never damped zoom either — damping a discrete wheel notch smears it over a second. Raise it only for trackpad-style continuous scroll.">
      <NumField label="Delta" pinId="in-delta" value={data.delta ?? 0} onChange={v => data.onChange?.('delta', v)} />
      <NumField label="Sens" value={data.sensitivity ?? 0.001} onChange={v => data.onChange?.('sensitivity', v)} />
      <NumField label="Damp" pinId="in-damping" value={data.damping ?? 0} onChange={v => data.onChange?.('damping', v)} />
      <CheckField label="Invert" checked={data.invert} onChange={v => data.onChange?.('invert', v)} />
    </Shell>
  )
}

export function CameraPanTargetNode({ data, selected }) {
  return (
    <Shell exec selected={selected} fired={isFired(data)} suppressed={useMotionSuppressed()} icon="✋" label="Camera Pan Target" minWidth={236}
      tip="Slide the camera's look-at along the screen's right/up axes. Scaled by distance so it feels the same at any zoom. Wire On Pointer Drag's out-dx/out-dy (typically the right button). Damp is the glide left after the drag ends (0 = instant, 0.95 = the shipped feel). The direction is fixed when the input arrives, so orbiting mid-glide won't curve an in-flight pan.">
      <NumField label="dx" pinId="in-dx" value={data.dx ?? 0} onChange={v => data.onChange?.('dx', v)} />
      <NumField label="dy" pinId="in-dy" value={data.dy ?? 0} onChange={v => data.onChange?.('dy', v)} />
      <NumField label="Sens" value={data.sensitivity ?? 0.0015} onChange={v => data.onChange?.('sensitivity', v)} />
      <NumField label="Damp" pinId="in-damping" value={data.damping ?? 0.95} onChange={v => data.onChange?.('damping', v)} />
    </Shell>
  )
}

export function CameraSetRigLimitsNode({ data, selected }) {
  return (
    <Shell exec selected={selected} fired={isFired(data)} icon="📏" label="Camera Set Rig Limits" minWidth={236}
      tip="Bound the rig: how close/far it can dolly, and how high/low it can pitch (degrees). This is what a 'preset' used to hardcode — it's now yours to set. Tightening a limit re-clamps the live camera immediately.">
      <NumField label="Min dist" value={data.minDist ?? 0.6} onChange={v => data.onChange?.('minDist', v)} />
      <NumField label="Max dist" value={data.maxDist ?? 0} onChange={v => data.onChange?.('maxDist', v)} />
      <NumField label="Min pitch°" value={data.minPitch ?? -89} onChange={v => data.onChange?.('minPitch', v)} />
      <NumField label="Max pitch°" value={data.maxPitch ?? 89} onChange={v => data.onChange?.('maxPitch', v)} />
      <div style={{ padding: '2px 12px 6px', color: '#2f6373', fontSize: 9 }}>max dist 0 = unbounded</div>
    </Shell>
  )
}

export function CameraSetRigPoseNode({ id, data, selected }) {
  return (
    <Shell exec selected={selected} fired={isFired(data)} icon="🎬" label="Camera Set Rig Pose" minWidth={244}
      tip="Place the camera at an absolute pose: yaw/pitch (degrees) around a target, at a distance. Blend ms > 0 eases instead of cutting. User input cancels an in-flight blend — a scripted move must never fight the hands on the mouse.">
      <NumField label="Yaw°" value={data.yaw ?? 0} onChange={v => data.onChange?.('yaw', v)} />
      <NumField label="Pitch°" value={data.pitch ?? 10} onChange={v => data.onChange?.('pitch', v)} />
      <NumField label="Dist" value={data.dist ?? 3} onChange={v => data.onChange?.('dist', v)} />
      <VectorField nodeId={id} label="Target" pinId="in-target"
        x={data.x ?? 0} y={data.y ?? 1.35} z={data.z ?? 0} fields={['x', 'y', 'z']}
        onChange={(field, v) => data.onChange?.(field, v)} />
      <NumField label="Blend ms" pinId="in-blend" value={data.blendMs ?? 0} onChange={v => data.onChange?.('blendMs', v)} />
    </Shell>
  )
}

export function SetCameraFovNode({ data, selected }) {
  return (
    <Shell exec selected={selected} fired={isFired(data)} icon="🔭" label="Set Camera FOV" minWidth={220}
      tip="Set the camera field of view in degrees. Wire in-fov, or type a value. Blend ms > 0 eases the change instead of cutting.">
      <NumField label="FOV°" pinId="in-fov" value={data.fov ?? 45} onChange={v => data.onChange?.('fov', v)} />
      <NumField label="Blend ms" pinId="in-blend" value={data.blendMs ?? 0} onChange={v => data.onChange?.('blendMs', v)} />
    </Shell>
  )
}

export function SetCameraTargetNode({ id, data, selected }) {
  const mode = data.mode ?? 'vector'
  return (
    <Shell exec selected={selected} fired={isFired(data)} icon="🎯" label="Set Camera Target" minWidth={236}
      tip="Point the camera at a world vector or the avatar head. Blend ms > 0 eases the look-at to the new target instead of cutting.">
      <EnumField label="Mode" value={mode} options={['vector', 'head']}
        onChange={v => data.onChange?.('mode', v)} />
      {mode === 'vector' && (
        <VectorField nodeId={id} label="Target" pinId="in-target"
          x={data.x} y={data.y} z={data.z} fields={['x', 'y', 'z']}
          onChange={(field, v) => data.onChange?.(field, v)} />
      )}
      <NumField label="Blend ms" pinId="in-blend" value={data.blendMs ?? 0} onChange={v => data.onChange?.('blendMs', v)} />
    </Shell>
  )
}

export function SetCameraCageNode({ id, data, selected }) {
  return (
    <Shell exec selected={selected} fired={isFired(data)} icon="🧰" label="Set Camera Cage" minWidth={248}
      tip="Confine the camera to a world-space box around the avatar so it can't dolly out through splat walls. Wire in-halfExtents, or type hx/hy/hz.">
      <CheckField label="Enabled" checked={data.enabled !== false} onChange={v => data.onChange?.('enabled', v)} />
      <VectorField nodeId={id} label="Half-ext" pinId="in-halfExtents"
        x={data.hx ?? 2.5} y={data.hy ?? 1.25} z={data.hz ?? 2.5} fields={['hx', 'hy', 'hz']}
        onChange={(field, v) => data.onChange?.(field, v)} />
      <NumField label="Margin" value={data.margin ?? 0.5} onChange={v => data.onChange?.('margin', v)} />
    </Shell>
  )
}

export function SetCameraSeeThroughNode({ data, selected }) {
  return (
    <Shell exec selected={selected} fired={isFired(data)} icon="👁️" label="Set Camera See-Through" minWidth={236}
      tip="Fade environment splats between the camera and the avatar head so geometry doesn't occlude the face when orbiting. Auto = fade only when the camera is close to / below the head (a proximity heuristic, not true occlusion).">
      <CheckField label="Enabled" checked={!!data.enabled} onChange={v => data.onChange?.('enabled', v)} />
      <NumField label="Radius" value={data.radius ?? 0.6} onChange={v => data.onChange?.('radius', v)} />
      <NumField label="Fade" value={data.fade ?? 0.3} onChange={v => data.onChange?.('fade', v)} />
      <CheckField label="Auto" checked={!!data.auto} onChange={v => data.onChange?.('auto', v)} />
      <NumField label="Auto near" value={data.autoNear ?? 1.2} onChange={v => data.onChange?.('autoNear', v)} />
      <CheckField label="Auto below" checked={data.autoBelow !== false} onChange={v => data.onChange?.('autoBelow', v)} />
    </Shell>
  )
}

export function SetCameraFollowNode({ id, data, selected }) {
  const space = data.space ?? 'local'
  return (
    <Shell exec selected={selected} fired={isFired(data)} icon="🛰️" label="Set Camera Follow" minWidth={248}
      tip="Continuously frame the avatar as it moves. Offset is the camera position relative to the avatar — 'local' rotates it by the avatar's facing so the camera trails on turns; 'world' keeps a fixed direction. Damping (0..1) smooths the chase; targetOffsetY biases the look-at up/down. Overrides the preset pose while enabled.">
      <CheckField label="Enabled" checked={!!data.enabled} onChange={v => data.onChange?.('enabled', v)} />
      <VectorField nodeId={id} label="Offset" pinId="in-offset"
        x={data.x ?? 0} y={data.y ?? 1.6} z={data.z ?? 3} fields={['x', 'y', 'z']}
        onChange={(field, v) => data.onChange?.(field, v)} />
      <EnumField label="Space" value={space} options={['local', 'world']}
        onChange={v => data.onChange?.('space', v)} />
      <NumField label="Damping" value={data.damping ?? 0.12} onChange={v => data.onChange?.('damping', v)} />
      <NumField label="Look Y+" value={data.targetOffsetY ?? 0} onChange={v => data.onChange?.('targetOffsetY', v)} />
    </Shell>
  )
}

export function GetCameraTransformNode({ data, selected }) {
  return (
    <Shell selected={selected} fired={isFired(data)} icon="🎞️" label="Get Camera Transform" minWidth={220}
      tip="Live camera { location, rotation, target, fov }. Wire into Branch / Print / Set nodes.">
      <div style={{ padding: '6px 12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, position: 'relative' }}>
        <span style={{ color: '#2f6373', fontSize: 10 }}>Transform</span>
        <Handle type="source" position={Position.Right} id="out-value" style={vecPin} />
      </div>
    </Shell>
  )
}

// ── Head-Tracked Camera (ENG-981, reworked to exec-wired) ────────────────────
// Ordinary exec node, like every other camera config node — the review that
// shipped this rework rejected the original declarative version ("presence +
// enabled IS the contract, no wires"): a node that acts while wired to
// nothing is illegible in an event graph where every OTHER node participates
// via wires. Unwired = fully inert (never arms, never syncs). Wire it from
// onConstruct for "armed at load, live-tunable through every editor edit"
// (the closest match to the old declarative behaviour), from beginPlay for
// "armed only once play starts", or from anywhere else — EventRuntime's
// headTrackedCamera exec case (see EventRuntime.js) calls
// headTrackedCamera.syncFromGraph(node) each time this fires. Deleting the
// node from the graph still tears the runtime down via App.jsx's
// subscribeEventGraph delete-teardown watcher (mirrors the voxyConfig
// watcher) — that path is presence-based by necessity (a deleted node can't
// fire an exec chain to say so), but arming/config never is.
// Field names below are the runtime's own HEAD_TRACK_DEFAULTS config keys —
// never hand-retype them, or a field silently stops reaching the runtime.
const HTD = HEAD_TRACK_DEFAULTS

function HeadTrackStateReadout({ state }) {
  return (
    <span style={{ fontSize: 9, color: state === 'error' || state === 'blocked' ? '#f0a860' : '#3a7080', letterSpacing: 0.5 }}>
      {state}
    </span>
  )
}

function RecenterButton() {
  return (
    <button
      type="button"
      onMouseDown={stop}
      onClick={e => { stop(e); headTrackedCamera.recenter() }}
      title="Re-zero the tracked offset at the current head position — this pokes the live runtime directly, it is not a graph edit."
      style={{
        fontSize: 9, padding: '2px 7px', borderRadius: 3, cursor: 'pointer',
        background: '#04121a', border: '1px solid #14384f', color: '#8ad0e0', fontFamily: 'var(--m-font)',
      }}
    >Recenter</button>
  )
}

export function HeadTrackedCameraNode({ data, selected }) {
  const state = useHeadTrackState()
  return (
    <Shell exec selected={selected} fired={isFired(data)} icon="📷" label="Head-Tracked Camera" minWidth={240}
      headerExtra={<><HeadTrackStateReadout state={state} /><RecenterButton /></>}
      tip="Wire from onConstruct (armed at load, live-tunable via every editor edit) or beginPlay (armed at play start) — unwired, this node is fully inert. Enabling shows a viewport pill; the webcam only starts on a click of that pill — camera permission is never requested automatically. Adds a parallax offset on top of the camera rig/follow — it never replaces them.">
      <CheckField label="Enabled" checked={!!data.enabled} onChange={v => data.onChange?.('enabled', v)} />
      <EnumField label="Mode" value={data.mode ?? HTD.mode} options={['pan', 'offAxis']} onChange={v => data.onChange?.('mode', v)} />
      <NumField label="Scale X" value={data.scaleX ?? HTD.scaleX} onChange={v => data.onChange?.('scaleX', v)} />
      <NumField label="Scale Y" value={data.scaleY ?? HTD.scaleY} onChange={v => data.onChange?.('scaleY', v)} />
      <NumField label="Scale Z" value={data.scaleZ ?? HTD.scaleZ} onChange={v => data.onChange?.('scaleZ', v)} />
      <NumField label="Max X (m)" value={data.maxX ?? HTD.maxX} onChange={v => data.onChange?.('maxX', v)} />
      <NumField label="Max Y (m)" value={data.maxY ?? HTD.maxY} onChange={v => data.onChange?.('maxY', v)} />
      <NumField label="Max Z (m)" value={data.maxZ ?? HTD.maxZ} onChange={v => data.onChange?.('maxZ', v)} />
      <NumField label="Smoothing" value={data.smoothing ?? HTD.smoothing} onChange={v => data.onChange?.('smoothing', v)} />
      <NumField label="Swivel" value={data.swivel ?? HTD.swivel} onChange={v => data.onChange?.('swivel', v)} />
      <CheckField label="Invert X" checked={!!data.invertX} onChange={v => data.onChange?.('invertX', v)} />
      <CheckField label="Invert Y" checked={!!data.invertY} onChange={v => data.onChange?.('invertY', v)} />
      <CheckField label="Invert Z" checked={!!data.invertZ} onChange={v => data.onChange?.('invertZ', v)} />
      <NumField label="Track FPS" value={data.trackFps ?? HTD.trackFps} onChange={v => data.onChange?.('trackFps', v)} />
    </Shell>
  )
}
